import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Receipt, DollarSign, AlertCircle, CalendarDays, CheckCircle2, Clock, XCircle, MessageSquare, X, CreditCard, Loader2, Shield, TrendingUp, Zap } from 'lucide-react';
import { TrialBanner } from '@/components/TrialBanner';
import { PaydaaiDebtCard } from '@/components/PaydaaiDebtCard';

const APPOINTMENT_TYPES = ['barber', 'beauty', 'dental', 'gym'];

const formatLimit = (n) => (n === null || n === undefined ? '∞' : n.toLocaleString('tr-TR'));

const UsageBar = ({ label, used, limit, testId }) => {
  const isUnlimited = limit === null || limit === undefined;
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-[#2563EB]';
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-[#64748B] font-medium">{label}</span>
        <span className="text-[#0F172A] font-semibold tabular-nums">
          {used.toLocaleString('tr-TR')} / {formatLimit(limit)}
        </span>
      </div>
      <div className="h-2 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
        <div
          className={`h-full ${isUnlimited ? 'bg-[#2563EB]/30' : color} transition-all duration-500`}
          style={{ width: isUnlimited ? '100%' : `${pct}%` }}
        />
      </div>
    </div>
  );
};

const PlanUsageCard = ({ info }) => {
  const { plan, plan_label, limits, usage } = info;
  const planColor = {
    randevu:  { bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-300' },
    tahsilat: { bg: 'bg-amber-100',  text: 'text-amber-800',  border: 'border-amber-300' },
    tam:      { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
    kurumsal: { bg: 'bg-slate-100',  text: 'text-slate-700',  border: 'border-slate-300' },
    // legacy
    mini:    { bg: 'bg-slate-100',  text: 'text-slate-700',  border: 'border-slate-300' },
    pro:     { bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-300' },
    premium: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  }[plan] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' };

  // Top-tier plans where "upgrade" doesn't make sense
  const canUpgrade = !['tam', 'kurumsal', 'premium'].includes(plan);

  return (
    <Card className="border border-[#E2E8F0] mb-6" data-testid="plan-usage-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-[#EFF6FF] shrink-0">
              <Zap className="w-5 h-5 text-[#2563EB]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#0F172A]">Plan Kullanımı</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-md border ${planColor.bg} ${planColor.text} ${planColor.border}`}
                  data-testid="plan-label"
                >
                  {plan_label}
                </span>
                {limits.monthly_price > 0 && (
                  <span className="text-xs text-[#64748B] tabular-nums" data-testid="plan-price">
                    ₺{limits.monthly_price.toLocaleString('tr-TR')}/ay
                  </span>
                )}
              </div>
            </div>
          </div>
          {canUpgrade && (
            <Link
              to="/admin/plan-upgrade"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#2563EB] hover:bg-[#1E4ED8] rounded-lg px-3.5 py-2 transition-colors shadow-sm"
              data-testid="plan-upgrade-cta"
            >
              <TrendingUp className="w-3.5 h-3.5" /> Planı Yükselt
            </Link>
          )}
        </div>

        <div className={`grid grid-cols-1 ${limits.allow_branches ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4`}>
          <UsageBar
            label="Müşteri"
            used={usage.customers || 0}
            limit={limits.max_customers}
            testId="usage-customers"
          />
          <UsageBar
            label="Aylık İşlem"
            used={usage.monthly_transactions || 0}
            limit={limits.monthly_transaction_limit}
            testId="usage-transactions"
          />
          {limits.allow_branches && (
            <UsageBar
              label="Şube"
              used={usage.branches || 0}
              limit={limits.max_branches}
              testId="usage-branches"
            />
          )}
        </div>

        {/* SMS Balance row */}
        {typeof info.sms_balance === 'number' && (
          <div className={`mt-4 pt-3 border-t border-[#E5E5E5] flex items-center justify-between flex-wrap gap-2 ${info.sms_low_balance ? 'text-amber-700' : 'text-[#595959]'}`} data-testid="sms-balance-row">
            <div className="flex items-center gap-2 text-sm">
              <span className={`w-2 h-2 rounded-full ${info.sms_low_balance ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`} />
              <span className="font-medium">SMS Bakiye:</span>
              <span className="tabular-nums" data-testid="sms-balance-value">{info.sms_balance.toLocaleString('tr-TR')}</span>
              {info.sms_low_balance && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">Bakiye bitiyor</span>
              )}
            </div>
            {info.sms_low_balance && (
              <a
                href="mailto:destek@paydaai.com?subject=SMS%20Paketi%20Sat%C4%B1n%20Alma"
                className="text-xs font-semibold text-[#2563EB] hover:underline"
                data-testid="sms-topup-cta"
              >
                SMS Paketi Al →
              </a>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const AdminDashboard = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const isAppointmentType = APPOINTMENT_TYPES.includes(user?.business_type);
  const labels = user?.labels || { people: 'Kullanıcılar', payment: 'Ödeme' };
  const isStaff = user?.role === 'staff';

  const [summary, setSummary] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [saMessages, setSaMessages] = useState([]);
  const [subStatus, setSubStatus] = useState(null);
  const [payMonths, setPayMonths] = useState(1);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    if (isAppointmentType) {
      fetchAppointmentData();
    } else {
      fetchSummary();
    }
    fetchSAMessages();
    fetchSubscription();
    fetchPlanInfo();
  }, []);

  // Check for returning from PayTR subscription payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('sub_session_id');
    if (sessionId) {
      setPolling(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!polling) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/subscription/check/${polling}`, { withCredentials: true });
        if (data.payment_status === 'paid') {
          toast.success('Abonelik ödemeniz başarılı! Süreniz uzatıldı.');
          setPolling(null);
          fetchSubscription();
          return;
        }
        attempts++;
        if (attempts < 8) setTimeout(poll, 2000);
        else { toast.info('Ödeme durumu kontrol ediliyor...'); setPolling(null); }
      } catch { setPolling(null); }
    };
    poll();
  }, [polling, API_URL]);

  const fetchSummary = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/summary`, { withCredentials: true });
      setSummary(data);
    } catch (e) { /* empty */ }
  };

  const fetchSAMessages = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/sa-messages`, { withCredentials: true });
      setSaMessages(data.filter(m => !m.read));
    } catch (e) { /* empty */ }
  };

  const fetchSubscription = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/subscription/status`, { withCredentials: true });
      setSubStatus(data);
    } catch (e) { /* empty */ }
  };

  const fetchPlanInfo = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/plan/info`, { withCredentials: true });
      setPlanInfo(data);
    } catch (e) { /* empty */ }
  };

  const markRead = async (msgId) => {
    try {
      await axios.put(`${API_URL}/api/admin/sa-messages/${msgId}/read`, {}, { withCredentials: true });
      setSaMessages(prev => prev.filter(m => m.id !== msgId));
    } catch (e) { /* empty */ }
  };

  const fetchAppointmentData = async () => {
    try {
      const [aptsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
      ]);
      setAppointments(aptsRes.data);
      setUserCount(usersRes.data.length);
    } catch (e) { /* empty */ }
  };

  const handleSubscriptionPay = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/subscription/pay`, {
        months: payMonths,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ödeme başlatılamadı');
      setPaying(false);
    }
  };

  const scheduled = appointments.filter(a => a.status === 'scheduled');
  const completed = appointments.filter(a => a.status === 'completed');
  const totalRevenue = completed.filter(a => a.price).reduce((sum, a) => sum + a.price, 0);
  const todayStr = new Date().toISOString().split('T')[0];
  const todayApts = appointments.filter(a => a.date === todayStr && a.status === 'scheduled');

  // Subscription helpers
  const daysLeft = subStatus?.days_left;
  const monthlyFee = subStatus?.monthly_fee || 0;
  const hasContract = subStatus?.contract_end;
  const isUrgent = daysLeft !== null && daysLeft <= 15;
  const isCritical = daysLeft !== null && daysLeft <= 5;

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="admin-dashboard-title">
            Yönetici Paneli
          </h1>
          <p className="text-base text-[#595959] mt-2">Genel bakış ve özet bilgiler</p>
        </div>

        <TrialBanner />
        <PaydaaiDebtCard />

        {/* Polling indicator */}
        {polling && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <p className="text-sm text-blue-700 font-medium">Abonelik ödeme durumu kontrol ediliyor...</p>
          </div>
        )}

        {/* Subscription Status Card */}
        {hasContract && (
          <Card className={`border-2 mb-6 ${isCritical ? 'border-red-300 bg-red-50/50' : isUrgent ? 'border-amber-300 bg-amber-50/50' : 'border-[#2563EB]/20 bg-[#F9F8F6]'}`} data-testid="subscription-card">
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Info */}
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`p-2.5 rounded-xl shrink-0 ${isCritical ? 'bg-red-100' : isUrgent ? 'bg-amber-100' : 'bg-[#2563EB]/10'}`}>
                    <Shield className={`w-5 h-5 ${isCritical ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-[#2563EB]'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A2E]">Abonelik Durumu</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`text-sm font-bold ${isCritical ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-[#2563EB]'}`} data-testid="days-left-text">
                        {daysLeft !== null ? (daysLeft <= 0 ? 'Süre dolmuş!' : `${daysLeft} gün kaldı`) : ''}
                      </span>
                      <span className="text-xs text-[#595959]">
                        ({subStatus.contract_start} → {subStatus.contract_end})
                      </span>
                    </div>
                    {monthlyFee > 0 && (
                      <p className="text-xs text-[#595959] mt-0.5">Aylık: {monthlyFee.toLocaleString('tr-TR')} TL</p>
                    )}
                  </div>
                </div>

                {/* Payment Section */}
                {monthlyFee > 0 && (
                  <div className="flex items-center gap-2 shrink-0" data-testid="subscription-pay-inline">
                    <Select value={String(payMonths)} onValueChange={(v) => setPayMonths(Number(v))}>
                      <SelectTrigger className="w-[130px] h-9 text-sm" data-testid="sub-months-inline">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 6, 12].map(m => (
                          <SelectItem key={m} value={String(m)}>{m} Ay - {(monthlyFee * m).toLocaleString('tr-TR')} TL</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleSubscriptionPay}
                      disabled={paying}
                      className="bg-[#2563EB] hover:bg-[#1E4ED8] gap-1.5 h-9 text-sm"
                      data-testid="extend-subscription-btn"
                    >
                      {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                      Süre Uzat
                    </Button>
                  </div>
                )}
              </div>

              {/* Payment History */}
              {subStatus?.payment_history && subStatus.payment_history.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                  <p className="text-xs font-medium text-[#595959] mb-1.5">Son Ödemeler:</p>
                  <div className="flex flex-wrap gap-2">
                    {subStatus.payment_history.slice(0, 3).map((ph) => (
                      <span key={ph.id} className={`text-xs px-2 py-1 rounded-full ${ph.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {ph.months} Ay - {ph.amount.toLocaleString('tr-TR')} TL {ph.payment_status === 'paid' ? '(Ödendi)' : '(Bekliyor)'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* SA Messages Banner */}
        {saMessages.length > 0 && (
          <div className="space-y-2 mb-6" data-testid="sa-messages-banner">
            {saMessages.map(msg => (
              <div key={msg.id} className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`sa-msg-${msg.id}`}>
                <MessageSquare className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-800">Sistem Yöneticisinden Mesaj</p>
                  <p className="text-sm text-blue-700 mt-1">{msg.message}</p>
                  <p className="text-xs text-blue-500 mt-1">{msg.created_at ? new Date(msg.created_at).toLocaleDateString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => markRead(msg.id)} title="Okundu" data-testid={`mark-read-${msg.id}`}><X className="w-4 h-4 text-blue-500" /></Button>
              </div>
            ))}
          </div>
        )}

        {/* Billing incomplete banner */}
        {!isStaff && user?.billing_complete === false && (
          <Link
            to="/admin/billing"
            className="mb-6 flex items-center gap-3 p-4 bg-[#FFF4E5] border-l-4 border-[#F5A524] rounded-lg hover:bg-[#FFE8C5] transition-all shadow-[0_4px_12px_rgba(245,165,36,0.08)]"
            data-testid="billing-incomplete-banner"
          >
            <AlertCircle className="w-5 h-5 text-[#F5A524] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#92400E]">Fatura bilgileriniz eksik</p>
              <p className="text-xs text-[#B45309] mt-0.5">Aylık abonelik faturanızın düzenlenebilmesi için vergi ve adres bilgilerinizi eksiksiz doldurun.</p>
            </div>
            <span className="text-xs font-semibold text-[#92400E] shrink-0">Tamamla →</span>
          </Link>
        )}

        {/* Plan & Quota Widget - sahip/işletme yönetici için */}
        {!isStaff && planInfo && (
          <PlanUsageCard info={planInfo} />
        )}

        {isAppointmentType ? (
          /* Appointment Dashboard */
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border border-[#E5E5E5]" data-testid="card-total-clients">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium text-[#595959]">Toplam {labels.people}</CardTitle>
                  <Users className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#1A1A1A]">{userCount}</div>
                </CardContent>
              </Card>

              <Card className="border border-[#E5E5E5]" data-testid="card-scheduled-count">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium text-[#595959]">Bekleyen Randevu</CardTitle>
                  <Clock className="w-5 h-5 text-yellow-600" strokeWidth={1.5} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#1A1A1A]">{scheduled.length}</div>
                </CardContent>
              </Card>

              <Card className="border border-[#E5E5E5]" data-testid="card-completed-count">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium text-[#595959]">Tamamlanan</CardTitle>
                  <CheckCircle2 className="w-5 h-5 text-[#16A34A]" strokeWidth={1.5} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#16A34A]">{completed.length}</div>
                </CardContent>
              </Card>

              <Card className="border border-[#E5E5E5]" data-testid="card-revenue">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium text-[#595959]">Toplam Gelir</CardTitle>
                  <DollarSign className="w-5 h-5 text-[#16A34A]" strokeWidth={1.5} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#16A34A]">{totalRevenue.toLocaleString('tr-TR')} TL</div>
                </CardContent>
              </Card>
            </div>

            {todayApts.length > 0 && (
              <Card className="border border-[#2563EB]" data-testid="today-appointments-card">
                <CardHeader>
                  <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
                    <CalendarDays className="w-5 h-5" />
                    Bugünkü Randevular ({todayApts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {todayApts.map((apt) => (
                      <div key={apt.id} className="flex items-center justify-between p-3 bg-[#F9F8F6] rounded-lg">
                        <div>
                          <p className="font-medium">{apt.client_name} - {apt.service}</p>
                          <p className="text-sm text-[#595959]">{apt.start_time} - {apt.end_time}</p>
                        </div>
                        {apt.price && <span className="font-medium text-[#2563EB]">{apt.price} TL</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          /* Payment Dashboard */
          summary ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="border border-[#E5E5E5]" data-testid="card-total-users">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium text-[#595959]">Toplam {labels.people}</CardTitle>
                  <Users className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#1A1A1A]">{summary.total_users}</div>
                </CardContent>
              </Card>

              <Card className="border border-[#E5E5E5]" data-testid="card-total-payments">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium text-[#595959]">Toplam {labels.payment}</CardTitle>
                  <Receipt className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#1A1A1A]">{summary.total_payments}</div>
                </CardContent>
              </Card>

              <Card className="border border-[#E5E5E5]" data-testid="card-total-paid">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium text-[#595959]">Tahsil Edilen</CardTitle>
                  <DollarSign className="w-5 h-5 text-[#16A34A]" strokeWidth={1.5} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#16A34A]">{summary.total_paid.toFixed(2)} TL</div>
                  <p className="text-sm text-[#595959] mt-1">{summary.paid_count} ödeme</p>
                </CardContent>
              </Card>

              <Card className="border border-[#E5E5E5]" data-testid="card-total-overdue">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-medium text-[#595959]">Gecikmiş</CardTitle>
                  <AlertCircle className="w-5 h-5 text-[#DC2626]" strokeWidth={1.5} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#DC2626]">{summary.total_overdue.toFixed(2)} TL</div>
                  <p className="text-sm text-[#595959] mt-1">{summary.overdue_count} ödeme</p>
                </CardContent>
              </Card>

              <Card className="border border-[#E5E5E5] col-span-full" data-testid="card-pending-summary">
                <CardHeader>
                  <CardTitle className="text-xl">Bekleyen {labels.payment}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#1A1A1A]">{summary.total_pending.toFixed(2)} TL</p>
                  <p className="text-sm text-[#595959] mt-1">{summary.pending_count} ödeme bekliyor</p>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12" data-testid="loading-summary">
              <p className="text-[#595959]">Yükleniyor...</p>
            </div>
          )
        )}
      </div>
    </PageLayout>
  );
};
